import React from 'react'
import { Table, DataTable, Button, SelectInline, InputInline, Input, PhoneInput, Icon } from '@arnsbogroup/react-voicearchive'
import useValidate, { is } from '@arnsbogroup/react-voicearchive/hooks/useValidate'
import { useIntl } from 'react-intl'
import messages from "./messages";
import useCurrentUser from 'hooks/useCurrentUser'

const Span = ({ children }) => <span>{ children }</span>

const Contacts = ( {customer, onChange, isCustomerDataComplete} ) => {
  if (!customer) {
      return null
  }
  const currentUser = useCurrentUser()
  const { formatMessage } = useIntl()
  const initialEmptyContact = {
    name: '',
    email: '',
    phone: {
      number: '',
      country: 'dk'
    },
  }
  const [newContact, setNewContact] = React.useState(initialEmptyContact);

  function createContactHandler() {
    newContact.phone = newContact.phone.number
    fetch(`/rest/v2/customer/${customer.uid}/contacts`, {
      method: "POST",
      body: JSON.stringify([newContact]),
    })
      .then(() => {
        toastr.success("Contact created");
        setNewContact(initialEmptyContact);
        onChange(customer, true); // force refresh to fetch new contact list
      })
  }

  function updateContact(contact) {
    fetch(`/rest/v2/user/${contact.uid}`, {
      method: "PUT",
      body: JSON.stringify(contact),
    })
    .then((response) => response.json())
    .then(({success, errors}) => {
      if (success) {
        toastr.success("Contact updated");
        onChange(customer, true); // force refresh to fetch new contact list
      } else {
        toastr.error(errors.join(', '))
      }
    })
  }

  const DeleteButton = ({ uid, name }) => (!customer.crmIsMaster) ? <Button
    size='sm'
    onClick={() => {
      if (confirm(`Are you sure you want to delete this contact (${name})`)) {
        fetch(`/rest/v2/customer/${customer.uid}/contacts`, {
          method: "DELETE",
          body: JSON.stringify([{ uid }]),
        })
          .then(() => {
            toastr.success("Contact deleted");
            onChange(customer, true); // force refresh to fetch new contact list
          })
      }
    }}
  >
    Delete
  </Button> : null;
  
  const ResetButton = ({ username, name }) => <Button
    size='sm'
    onClick={() => {
      if (confirm(`Are you sure you want to send this contact a password reset email (${name})`)) {
        toastr.info('Sending e-mail..');
        fetch(`/user/recover.json?identifier=${username}`, { method: 'POST' })
        .then((response) => response.json())
        .then((response) => {
          if (response.success) {
            toastr.success(`Password reset link was sent to ${name}`);
          } else {
            toastr.error(response.errors.join('. '));
          }
        });
      }
    }}
  >
    Send password reset email
  </Button>
  
  const ImpersonateButton = ({ uid, name }) => <Button
    size='sm'
    onClick={() => {
      if (confirm(`Are you sure you want to impersonate this contact (${name})`)) {
        currentUser.impersonate(uid)
      }
    }}
  >
    Impersonate
  </Button>

  const HLExclude = (contact) => <SelectInline
    labelClassName="text-xs"
    value={ { value: contact.hl_exclude, label: (contact.hl_exclude == '1') ? 'Yes' : 'No' } }
    options={[
      { value: '1', label: 'Yes' },
      { value: '0', label: 'No' },
    ]}
    disabled={customer.crmIsMaster}
    onChange={(selected) => { updateContact({ ...contact, hl_exclude: selected.value }) } }
  />

  const UsernameSpan = (contact) => <InputInline
    labelClassName="text-xs"
    placeholder="Username"
    value={ contact.username }
    onChange={({ target: { value } }) => {
      updateContact({ ...contact, username: value })
    }}
  />

  const NameSpan = (contact) => <InputInline
    labelClassName="text-xs"
    placeholder="Name"
    disabled={customer.crmIsMaster}
    value={ contact.name }
    onChange={({ target: { value } }) => {
      updateContact({ ...contact, name: value })
    }}
  />

  const TelephoneSpan = (contact) => <InputInline
    labelClassName="text-xs"
    placeholder="Telephone"
    disabled={customer.crmIsMaster}
    value={ contact.telephone }
    onChange={({ target: { value } }) => {
      updateContact({ ...contact, telephone: value })
    }}
  />
  
  const EmailSpan = (contact) => <InputInline
    labelClassName="text-xs"
    placeholder="Email"
    disabled={customer.crmIsMaster}
    value={ contact.email }
    onChange={({ target: { value } }) => {
      updateContact({ ...contact, email: value })
    }}
  />

  return (
    <div className='p-4'>
      {!customer.crmIsMaster && (

        <div className='grid lg:grid-cols-6 sm:grid-cols-3 gap-4 my-4'>
          <div>
            <Input 
              labelClassName="text-xs"
              placeholder="Name"
              value={newContact.name}
              onChange={({ target: { value } }) => {
                setNewContact({ ...newContact, name: value })
              }}
            />
          </div>
          
          <div>
            <Input 
              labelClassName="text-xs"
              placeholder="Email"
              value={newContact.email}
              onChange={({ target: { value } }) => {
                setNewContact({ ...newContact, email: value })
              }}
            />
          </div>

          <div>
            <PhoneInput
              country={initialEmptyContact.phone.country}
              enableSearch
              disableSearchIcon
              searchPlaceholder="Search"
              searchNotFound="Not found"
              value={newContact.phone.number}
              onChange={(phone, country) => {
                const data = {
                  number: '+' + phone,
                  country: country.countryCode
                };
                setNewContact({ ...newContact, phone: data })
              }}
            />
          </div>

          <div className='my-auto flex space-x-2'>
            <Button
              size='sm'
              disabled={!newContact.name || !newContact.email || !isCustomerDataComplete}
              onClick={() => {
                createContactHandler();
              }}
              errors={!isCustomerDataComplete ? formatMessage(messages.dataNotComplete) : ''}
            >
              Add Contact
            </Button>
            { !isCustomerDataComplete &&
            <div className='my-auto'>
              <Icon className="text-negative" icon={["fa","info-circle"]} title={formatMessage(messages.dataNotComplete)}/>
            </div>
            }
          </div>
      </div>
      )}
      {customer.contacts.length > 0 ? (
        <DataTable
          className="w-full"
          components={{
          Tr: Table.Tr,
          Td: Table.Td
          }}
          head={{
            uid: { label: 'ID' },
            username: { label: 'Username' },
            name: { label: 'Name' },
            telephone: { label: 'Telephone' },
            email: { label: 'Email' },
            hl_exclude: { label: 'Exclude from HeyLoyalty' },
            delete: { label: ' ' },
            reset: { label: ' ' },
            impersonate: { label: ' ' },
          }}
          body={{
            uid: Span,
            username: UsernameSpan,
            name: NameSpan,
            telephone: TelephoneSpan,
            email: EmailSpan,
            hl_exclude: HLExclude,

            delete: DeleteButton,
            reset: ResetButton,
            impersonate: ImpersonateButton,
          }}
          entries={customer.contacts}
        />
      ) : (
        <div className="text-center text-gray-500">No contacts</div>
      )}
    </div>
  )
}

export default Contacts
